// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DeviceBreakdown
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS device-breakdown visualization that
 * compares desktop, tablet, and mobile traffic. The component pairs a
 * proportional bar with a simple legend for quick distribution scans.
 */

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Progress,
} from "@geenius/ui/solidjs";
import { type Component, createEffect, For, onMount } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

function labelProgressBar(node: HTMLDivElement | null, label: string): void {
  node
    ?.querySelector('[role="progressbar"]')
    ?.setAttribute("aria-label", label);
}

const AccessibleProgress: Component<{
  label: string;
  value: number;
  max: number;
}> = (props) => {
  let root: HTMLDivElement | undefined;

  const applyLabel = (): void => {
    labelProgressBar(root ?? null, props.label);
  };

  onMount(() => {
    applyLabel();
  });

  createEffect(() => {
    props.label;
    props.value;
    props.max;
    queueMicrotask(() => {
      applyLabel();
    });
  });

  return (
    <div ref={root}>
      <Progress value={props.value} max={props.max} />
    </div>
  );
};

/**
 * Props for rendering the SolidJS device-breakdown widget.
 *
 * @property breakdown Device counts grouped by desktop, tablet, and mobile.
 * @property error Optional error value rendered instead of the device rows.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the wrapper.
 */
export interface DeviceBreakdownProps {
  breakdown: { desktop: number; tablet: number; mobile: number };
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS device-breakdown widget.
 *
 * @param props Device counts and optional wrapper class.
 * @returns A device distribution widget for analytics dashboards.
 */
export const DeviceBreakdown: Component<DeviceBreakdownProps> = (props) => {
  const total = (): number =>
    props.breakdown.desktop + props.breakdown.tablet + props.breakdown.mobile ||
    1;
  const pct = (value: number): string =>
    `${((value / total()) * 100).toFixed(1)}%`;
  const copy = createAnalyticsCopy();

  const segments = () => [
    {
      device: "desktop",
      value: props.breakdown.desktop,
      icon: "💻",
      variant: "secondary" as const,
    },
    {
      device: "tablet",
      value: props.breakdown.tablet,
      icon: "📟",
      variant: "warning" as const,
    },
    {
      device: "mobile",
      value: props.breakdown.mobile,
      icon: "📱",
      variant: "success" as const,
    },
  ];

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader class="pb-0">
          <CardTitle class="text-sm" data-analytics-section-title>
            {copy.t("analytics.devices.title")}
          </CardTitle>
        </CardHeader>
        <CardContent class="pt-4">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card class={props.class}>
      <CardHeader class="pb-0">
        <CardTitle class="text-sm" data-analytics-section-title>
          {copy.t("analytics.devices.title")}
        </CardTitle>
      </CardHeader>
      <CardContent class="grid gap-4 pt-4" data-analytics-device-breakdown>
        <For each={segments()}>
          {(segment) => (
            <div
              class="grid gap-2"
              data-analytics-device-item
              data-device={segment.device}
            >
              <div class="flex items-center justify-between gap-3">
                <Badge variant={segment.variant}>
                  {segment.icon} {copy.deviceLabel(segment.device)}
                </Badge>
                <span
                  class="text-sm font-medium text-muted-foreground"
                  data-analytics-device-pct
                >
                  {pct(segment.value)}
                </span>
              </div>
              <AccessibleProgress
                label={`${copy.deviceLabel(segment.device)} traffic share`}
                value={segment.value}
                max={total()}
              />
            </div>
          )}
        </For>
      </CardContent>
    </Card>
  );
};
